/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0; you may not use this file except in compliance with the Elastic License
 * 2.0.
 */

// DO NOT MODIFY THIS FILE BY HAND. IT IS GENERATED BY
// `yarn workspace @elastic/esql-definitions generate` FROM
// `elasticsearch/esql/x-pack-esql/definition/functions/json_extract.json`.

import type { FunctionDefinition } from '../../definition_types';

/**
 * Extracts a value from a JSON string using a subset of
 * JSONPath syntax.
 *
 * @example
 * ROW log = """{"severity":"ERROR","body":"Payment processing failed"}"""
 * | EVAL severity = JSON_EXTRACT(log, "severity")
 *
 * @example
 * ROW log = """{"severity":"ERROR","body":"Payment processing failed"}"""
 * | EVAL severity = JSON_EXTRACT(log, "$.severity")
 *
 * @example
 * ROW log = """{"resource":{"service":{"name":"order-service"}}}"""
 * | EVAL svc = JSON_EXTRACT(log, "resource.service.name")
 *
 * @example
 * FROM json_logs
 * | EVAL svc = JSON_EXTRACT(payload, "resource['service.name']")
 * | KEEP @timestamp, source, svc
 * | SORT @timestamp
 * | LIMIT 3
 *
 * @example
 * ROW log = """{"spans":[{"name":"auth","duration":12},{"name":"db-query","duration":45}]}"""
 * | EVAL span = JSON_EXTRACT(log, "spans[1].name")
 *
 * @example
 * ROW log = """{"resource":{"service.name":"api-gateway","host.name":"api-server-03"},"severity":"INFO"}"""
 * | EVAL resource = JSON_EXTRACT(log, "resource")
 *
 * @example
 * ROW json = """["a","b","c"]"""
 * | EVAL val = JSON_EXTRACT(json, "$[1]")
 * | KEEP val
 *
 * @example
 * ROW log = """{"trace":{"spans":[{"name":"auth","events":[{"type":"start"},{"type":"end"}]},{"name":"db","events":[{"type":"query"}]}]}}"""
 * | EVAL event = JSON_EXTRACT(log, "trace.spans[0].events[1].type")
 * | KEEP event
 */
const definition: FunctionDefinition = {
  type: 'scalar',
  name: 'json_extract',
  signatures: [
    {
      params: [
        {
          name: 'string',
          type: '_source',
          optional: false,
        },
        {
          name: 'path',
          type: 'keyword',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'string',
          type: '_source',
          optional: false,
        },
        {
          name: 'path',
          type: 'text',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'string',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'path',
          type: 'keyword',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'string',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'path',
          type: 'text',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'string',
          type: 'text',
          optional: false,
        },
        {
          name: 'path',
          type: 'keyword',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'string',
          type: 'text',
          optional: false,
        },
        {
          name: 'path',
          type: 'text',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
  ],
  preview: false,
  snapshotOnly: false,
};

export default definition;
